/**
 * Helpers for public shipment tracking URLs:
 *   /track/:shipmentId  (consignment_no preferred)
 *   /:shipmentId  (single-segment)
 *   /track-shipment?shipment_id=...  (QR / legacy links)
 */

/**
 * Static app/track path labels that must never be mistaken for a consignment
 * number. Without this guard, a static link like /public-track-shipment falls
 * through to the /:shipmentId catch-all and prefills the field with the label.
 */
export const RESERVED_TRACK_LABELS = new Set([
  'track',
  'track-shipment',
  'track_shipment',
  'trackshipment',
  'public-track-shipment',
  'public_track_shipment',
  'public-track',
  'public_track',
  'shipment',
]);

export function isReservedTrackLabel(value) {
  if (!value || typeof value !== 'string') return false;
  return RESERVED_TRACK_LABELS.has(value.trim().toLowerCase());
}

export function isLikelyShipmentId(value) {
  if (!value || typeof value !== 'string') return false;
  const id = value.trim();
  if (id.length < 4 || id.length > 30) return false;
  // Reserved static labels are page routes, not consignment numbers
  if (isReservedTrackLabel(id)) return false;
  // 8-digit consignment numbers are the primary tracking key
  if (/^\d{8}$/.test(id)) return true;
  return /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/.test(id);
}

export function parseShipmentIdFromSearch(searchParams) {
  if (!searchParams) return '';
  for (const key of ['shipment_id', 'shipmentId', 'consignment_no', 'id']) {
    const value = searchParams.get(key);
    if (value && isLikelyShipmentId(value)) {
      return value.trim();
    }
  }
  return '';
}

export function resolvePublicTrackShipmentId(routeShipmentId, searchParams) {
  if (routeShipmentId) {
    const fromRoute = decodeURIComponent(routeShipmentId).trim();
    if (isLikelyShipmentId(fromRoute)) return fromRoute;
  }
  return parseShipmentIdFromSearch(searchParams);
}

export function isPublicShipmentTrackPath(pathname) {
  if (!pathname || pathname === '/') return false;
  if (pathname.startsWith('/track/')) {
    const id = decodeURIComponent(pathname.slice('/track/'.length).split('/')[0] || '');
    return isLikelyShipmentId(id);
  }
  const match = pathname.match(/^\/([^/]+)$/);
  if (!match) return false;
  return isLikelyShipmentId(decodeURIComponent(match[1]));
}

export function buildPublicTrackPath(shipmentId) {
  const id = encodeURIComponent(String(shipmentId || '').trim());
  return `/${id}`;
}
